import { getWorkflowStatus } from "api/applications";

/**
 * Maps backend workflow step enum to frontend { section, slug }.
 * Used when navigating to the correct new-application step for a lead.
 */
const WORKFLOW_STEP_TO_PATH: Record<
  string,
  { section: "discussion" | "filing" | "status"; slug: string }
> = {
  S1_01_PRELIMINARY_DISCUSSION: {
    section: "discussion",
    slug: "preliminary-discussion-with-the-client",
  },
  S1_02_SEARCH_CHECKLIST_TO_CLIENT: {
    section: "discussion",
    slug: "check-list-for-search-to-client",
  },
  S1_03_SEARCH_DETAILS_FROM_CLIENT: {
    section: "discussion",
    slug: "details-for-search-from-client",
  },
  S1_04_ADVANCE_INVOICE_TO_CLIENT: {
    section: "discussion",
    slug: "invoice-of-advance-amount-to-client",
  },
  S1_05_ADVANCE_AMOUNT_FROM_CLIENT: {
    section: "discussion",
    slug: "advance-amount-from-client",
  },
  S1_06_CLASS_LIST_TO_CLIENT: {
    section: "discussion",
    slug: "class-list-to-client",
  },
  S1_07_SEARCH_REPORT_TO_CLIENT: {
    section: "discussion",
    slug: "search-report-to-client",
  },
  S1_08_LEGAL_ADVICE: { section: "discussion", slug: "legal-advice" },
  S1_08B_NEW_MARK_FROM_CLIENT: {
    section: "discussion",
    slug: "legal-advice",
  },
  S1_09_PROPOSAL_TO_CLIENT: {
    section: "discussion",
    slug: "proposal-to-client",
  },
  S1_10_CONFIRMATION_FROM_CLIENT: {
    section: "discussion",
    slug: "confirmation-from-client",
  },
  S1_11_INVOICE_TO_CLIENT: {
    section: "discussion",
    slug: "invoice-to-client-final",
  },
  S1_12_PAYMENT_FROM_CLIENT: {
    section: "discussion",
    slug: "payment-from-client",
  },
  S2_01_DOC_CHECKLIST_TO_CLIENT: {
    section: "filing",
    slug: "check-list-of-documents-to-client",
  },
  S2_02_DOCUMENTS_FROM_CLIENT: {
    section: "filing",
    slug: "documents-from-client",
  },
  S2_03_FILING_DETAILS_TO_CLIENT: {
    section: "filing",
    slug: "filing-details-to-client",
  },
  S2_04_FILING_DETAILS_CONFIRMATION: {
    section: "filing",
    slug: "confirmation-of-filing-details",
  },
  S2_05_STAMP_DETAILS_TO_VENDOR: {
    section: "filing",
    slug: "stamp-paper-details-to-vendor",
  },
  S2_06_STAMP_PAPER_FROM_VENDOR: {
    section: "filing",
    slug: "stamp-paper-from-vendor",
  },
  S2_07_POA_PREPARATION: {
    section: "filing",
    slug: "preparation-of-power-of-attorney",
  },
  S2_08_USER_AFFIDAVIT_PREPARATION: {
    section: "filing",
    slug: "preparation-of-user-affidavit",
  },
  S2_09_SIGNATURE_FROM_CLIENT: {
    section: "filing",
    slug: "signature-from-client",
  },
  S2_10_NOTARISATION: {
    section: "filing",
    slug: "notarisation-of-documents",
  },
  S2_11_SCANNING_OF_DOCUMENTS: {
    section: "filing",
    slug: "scanning-of-documents",
  },
  S2_12_UPLOADING_APPLICATION_DETAILS: {
    section: "filing",
    slug: "uploading-application-details",
  },
  S2_13_FILING_OF_APPLICATION: {
    section: "filing",
    slug: "filing-of-application",
  },
  S2_14_ACKNOWLEDGEMENT_TO_CLIENT: {
    section: "filing",
    slug: "acknowledgement-to-client",
  },
  S2_15_EXPEDITED_FILING: {
    section: "filing",
    slug: "filing-of-expedited-application",
  },
  S2_16_INVOICE_TO_CLIENT_EXPEDITED: {
    section: "filing",
    slug: "invoice-to-client-for-expedited-application",
  },
  S2_17_PAYMENT_FROM_CLIENT_EXPEDITED: {
    section: "filing",
    slug: "payment-from-client-expedited",
  },
};

const DEFAULT_STEP = {
  section: "discussion" as const,
  slug: "preliminary-discussion-with-the-client",
};

/**
 * Resolves the in-app path for a new-application lead's most recently completed
 * workflow step so the user lands directly on it from pending items. The optional
 * `preferLastCompleted` flag is preserved for backwards compatibility with the
 * /dashboard/applications table; the resolver now defaults to last completed in
 * either mode.
 */
export async function getNewApplicationPathForLead(
  leadId: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _options: { preferLastCompleted?: boolean } = {}
): Promise<string> {
  try {
    const status = await getWorkflowStatus(leadId);
    const lastCompleted = [...(status.steps ?? [])]
      .reverse()
      .find((s) => s.isCompleted)?.slug;
    const step =
      lastCompleted ?? status.maxStepReached ?? status.currentStep ?? null;
    let pathSpec = step ? WORKFLOW_STEP_TO_PATH[step] : null;
    if (!pathSpec && step?.startsWith("S3_")) {
      pathSpec = { section: "status", slug: "overview" };
    }
    const { section, slug } = pathSpec ?? DEFAULT_STEP;
    return `/dashboard/applications/new-application/${section}/${slug}/${leadId}`;
  } catch {
    return `/dashboard/applications/new-application/discussion/preliminary-discussion-with-the-client/${leadId}`;
  }
}
